import React from "react";
import { Modal } from "react-bootstrap";
import Resources from "../../../types/Resources";
import ChecklistRadioOptions from "../../Common/ChecklistRadioOptions";
import ScaffoldingConstructionChecklistFormState from "../../../types/forms/Permits/Conditions/ScaffoldingConstructionChecklistFormState";

type ScaffoldingConstructionChecklistModalProps = {
    resources: Resources;
    formState: ScaffoldingConstructionChecklistFormState;
    onChange: (
        fn: (prevValue: ScaffoldingConstructionChecklistFormState) => ScaffoldingConstructionChecklistFormState
    ) => void;
    show: boolean;
    onHide: () => void;
    canManageContent: boolean;
};

function ScaffoldingConstructionChecklistModal({
    resources,
    formState,
    onChange,
    show,
    onHide,
    canManageContent
}: ScaffoldingConstructionChecklistModalProps) {
    function changeHandler(key: keyof ScaffoldingConstructionChecklistFormState, value: any) {
        onChange((prevValue) => {
            const updatedFormState: ScaffoldingConstructionChecklistFormState = {
                ...prevValue,
                [key]: {
                    ...prevValue[key],
                    value
                }
            };

            return updatedFormState;
        });
    }

    return (
        <Modal show={show} onHide={onHide} size="xl">
            <Modal.Header closeButton>
                <h1 className="h5">{resources.get("ScaffoldingConstructionChecklist")}</h1>
            </Modal.Header>
            <Modal.Body>
                <ul className="list-unstyled">
                    <li className="m-3">
                        <ChecklistRadioOptions
                            id="will-be-built-in-pumping-station"
                            labelText={resources.get("WillBeBuiltInPumpingStation")}
                            onChange={(updatedValue) => changeHandler("willBeBuiltInPumpingStation", updatedValue)}
                            resources={resources}
                            value={formState.willBeBuiltInPumpingStation.value}
                            readOnly={!canManageContent}
                        />
                        <div className="text-muted w-75">{resources.get("WillBeBuiltInPumpingStationSubText")}</div>
                    </li>
                    <li className="m-3">
                        <ChecklistRadioOptions
                            id="will-be-built-on-quay"
                            labelText={resources.get("WillBeBuiltOnQuay")}
                            onChange={(updatedValue) => changeHandler("willBeBuiltOnQuay", updatedValue)}
                            resources={resources}
                            value={formState.willBeBuiltOnQuay.value}
                            readOnly={!canManageContent}
                        />
                        <div className="text-muted w-75">{resources.get("WillBeBuiltOnQuaySubText")}</div>
                    </li>
                    <li className="m-3">
                        <ChecklistRadioOptions
                            id="is-fall-protection-harness-needed"
                            labelText={resources.get("IsFallProtectionHarnessNeeded")}
                            onChange={(updatedValue) => changeHandler("isFallProtectionHarnessNeeded", updatedValue)}
                            resources={resources}
                            value={formState.isFallProtectionHarnessNeeded.value}
                            readOnly={!canManageContent}
                        />
                    </li>
                    <li className="m-3">
                        <ChecklistRadioOptions
                            id="is-harness-inspected-within-one-year"
                            labelText={resources.get("IsHarnessInspectedWithinOneYear")}
                            onChange={(updatedValue) => changeHandler("isHarnessInspectedWithinOneYear", updatedValue)}
                            resources={resources}
                            value={formState.isHarnessInspectedWithinOneYear.value}
                            readOnly={!canManageContent}
                        />
                    </li>
                    <li className="m-3">
                        <ChecklistRadioOptions
                            id="is-securely-connected-to-fall-protection"
                            labelText={resources.get("ScaffoldingConstruction_IsSecurelyConnectedToFallProtection")}
                            onChange={(updatedValue) =>
                                changeHandler("isSecurelyConnectedToFallProtection", updatedValue)
                            }
                            resources={resources}
                            value={formState.isSecurelyConnectedToFallProtection.value}
                            readOnly={!canManageContent}
                        />
                    </li>
                    <li className="m-3">
                        <ChecklistRadioOptions
                            id="will-have-approved-validity-certificate"
                            labelText={resources.get("WillHaveApprovedValidityCertificate")}
                            onChange={(updatedValue) =>
                                changeHandler("willHaveApprovedValidityCertificate", updatedValue)
                            }
                            resources={resources}
                            value={formState.willHaveApprovedValidityCertificate.value}
                            readOnly={!canManageContent}
                        />
                    </li>
                    <li className="m-3">
                        <ChecklistRadioOptions
                            id="will-have-sufficiently-high-guard-rails"
                            labelText={resources.get("WillHaveSufficientlyHighGuardRails")}
                            onChange={(updatedValue) =>
                                changeHandler("willHaveSufficientlyHighGuardRails", updatedValue)
                            }
                            resources={resources}
                            value={formState.willHaveSufficientlyHighGuardRails.value}
                            readOnly={!canManageContent}
                        />
                    </li>
                    <li className="m-3">
                        <ChecklistRadioOptions
                            id="will-toeboards-be-mounted"
                            labelText={resources.get("WillToeboardsBeMounted")}
                            onChange={(updatedValue) => changeHandler("willToeboardsBeMounted", updatedValue)}
                            resources={resources}
                            value={formState.willToeboardsBeMounted.value}
                            readOnly={!canManageContent}
                        />
                    </li>
                    <li className="m-3">
                        <ChecklistRadioOptions
                            id="will-have-loose-planks"
                            labelText={resources.get("WillHaveLoosePlanks")}
                            onChange={(updatedValue) => changeHandler("willHaveLoosePlanks", updatedValue)}
                            resources={resources}
                            value={formState.willHaveLoosePlanks.value}
                            readOnly={!canManageContent}
                        />
                    </li>
                    <li className="m-3">
                        <ChecklistRadioOptions
                            id="is-risk-of-stepping-through-after-completion"
                            labelText={resources.get("IsRiskOfSteppingThroughAfterCompletion")}
                            onChange={(updatedValue) =>
                                changeHandler("isRiskOfSteppingThroughAfterCompletion", updatedValue)
                            }
                            resources={resources}
                            value={formState.isRiskOfSteppingThroughAfterCompletion.value}
                            readOnly={!canManageContent}
                        />
                    </li>
                    <li className="m-3">
                        <ChecklistRadioOptions
                            id="is-back-protection-mounted-if-ladder-above-4m"
                            labelText={resources.get("IsBackProtectionMountedIfLadderAbove4m")}
                            onChange={(updatedValue) =>
                                changeHandler("isBackProtectionMountedIfLadderAbove4m", updatedValue)
                            }
                            resources={resources}
                            value={formState.isBackProtectionMountedIfLadderAbove4m.value}
                            readOnly={!canManageContent}
                        />
                    </li>
                    <li className="m-3">
                        <ChecklistRadioOptions
                            id="are-escape-routes-cleared"
                            labelText={resources.get("AreEscapeRoutesCleared")}
                            onChange={(updatedValue) => changeHandler("areEscapeRoutesCleared", updatedValue)}
                            resources={resources}
                            value={formState.areEscapeRoutesCleared.value}
                            readOnly={!canManageContent}
                        />
                    </li>
                    <li className="m-3">
                        <ChecklistRadioOptions
                            id="has-scaffolding-damage"
                            labelText={resources.get("HasScaffoldingDamage")}
                            onChange={(updatedValue) => changeHandler("hasScaffoldingDamage", updatedValue)}
                            resources={resources}
                            value={formState.hasScaffoldingDamage.value}
                            readOnly={!canManageContent}
                        />
                    </li>
                    <li className="m-3">
                        <ChecklistRadioOptions
                            id="has-oil-spill"
                            labelText={resources.get("HasOilSpill")}
                            onChange={(updatedValue) => changeHandler("hasOilSpill", updatedValue)}
                            resources={resources}
                            value={formState.hasOilSpill.value}
                            readOnly={!canManageContent}
                        />
                    </li>
                    <li className="m-3">
                        <ChecklistRadioOptions
                            id="has-ice"
                            labelText={resources.get("HasIce")}
                            onChange={(updatedValue) => changeHandler("hasIce", updatedValue)}
                            resources={resources}
                            value={formState.hasIce.value}
                            readOnly={!canManageContent}
                        />
                    </li>
                    <li className="m-3">
                        <ChecklistRadioOptions
                            id="is-passage-affected-by-scaffolding"
                            labelText={resources.get("IsPassageAffectedByScaffolding")}
                            onChange={(updatedValue) => changeHandler("isPassageAffectedByScaffolding", updatedValue)}
                            resources={resources}
                            value={formState.isPassageAffectedByScaffolding.value}
                            readOnly={!canManageContent}
                        />
                    </li>
                    <li className="m-3">
                        <ChecklistRadioOptions
                            id="is-passage-marked-and-collision-protection-installed"
                            labelText={resources.get("IsPassageMarkedAndCollisionProtectionInstalled")}
                            onChange={(updatedValue) =>
                                changeHandler("isPassageMarkedAndCollisionProtectionInstalled", updatedValue)
                            }
                            resources={resources}
                            value={formState.isPassageMarkedAndCollisionProtectionInstalled.value}
                            readOnly={!canManageContent}
                        />
                    </li>
                </ul>
            </Modal.Body>
        </Modal>
    );
}

export default ScaffoldingConstructionChecklistModal;
